/**
 * Account fields the PRD 06 revamp adds: Source and the Zoho customer / vendor ID.
 * Pure helpers, shared by the list, the Add Zoho ID modal and the account profile.
 */

/** Where an account was added from (PRD 06 §6). Set at creation and read-only. */
export type TAccountSourceKey = "no_code" | "zoho_crm" | "cristalina" | "slackbot"

export const ACCOUNT_SOURCES: { id: TAccountSourceKey; title: string }[] = [
    { id: "no_code", title: "No Code" },
    { id: "zoho_crm", title: "Zoho CRM" },
    { id: "cristalina", title: "Cristalina" },
    { id: "slackbot", title: "Slackbot" },
]

// The values the list returns before the migration, which moves CRM → Zoho CRM and Books → No Code.
const SOURCE_ALIASES: Record<string, TAccountSourceKey> = {
    no_code: "no_code",
    nocode: "no_code",
    "no code": "no_code",
    books: "no_code",
    zoho_crm: "zoho_crm",
    "zoho crm": "zoho_crm",
    crm: "zoho_crm",
    cristalina: "cristalina",
    slackbot: "slackbot",
    slack_bot: "slackbot",
}

/** The source as one of the four, or null when the account has none (or one we do not know). */
export const accountSource = (raw: string | null | undefined) => {
    const key = raw ? SOURCE_ALIASES[raw.trim().toLowerCase()] : undefined
    return key ? ACCOUNT_SOURCES.find(source => source.id === key)! : null
}

// What the list stores today for each source; the filter sends them with the key until the migration rewrites them.
const PRE_MIGRATION_VALUES: Record<TAccountSourceKey, string[]> = {
    no_code: ["Nocode", "Books"],
    zoho_crm: ["CRM"],
    cristalina: ["Cristalina"],
    slackbot: ["slack_bot"],
}

/** The More filters → Source value: each picked key, and the values it had before the migration. */
export const sourceFilterValues = (keys: TAccountSourceKey[]) =>
    keys.flatMap(key => [key, ...PRE_MIGRATION_VALUES[key]])

/**
 * A GSTIN: 2-digit state code, 10-character PAN, entity number, "Z", check character
 * (e.g. 06ABCDE1234F1Z5).
 */
export const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/

/** GST state codes, keyed by state name with case, spaces and punctuation stripped ("&" read as "and"). */
const GST_STATE_CODES: Record<string, string> = {
    jammuandkashmir: "01",
    himachalpradesh: "02",
    punjab: "03",
    chandigarh: "04",
    uttarakhand: "05",
    uttaranchal: "05",
    haryana: "06",
    delhi: "07",
    nctofdelhi: "07",
    newdelhi: "07",
    rajasthan: "08",
    uttarpradesh: "09",
    bihar: "10",
    sikkim: "11",
    arunachalpradesh: "12",
    nagaland: "13",
    manipur: "14",
    mizoram: "15",
    tripura: "16",
    meghalaya: "17",
    assam: "18",
    westbengal: "19",
    jharkhand: "20",
    odisha: "21",
    orissa: "21",
    chhattisgarh: "22",
    chattisgarh: "22",
    madhyapradesh: "23",
    gujarat: "24",
    damananddiu: "26",
    dadraandnagarhaveli: "26",
    dadraandnagarhavelianddamananddiu: "26",
    thedadraandnagarhavelianddamananddiu: "26",
    maharashtra: "27",
    karnataka: "29",
    goa: "30",
    lakshadweep: "31",
    kerala: "32",
    tamilnadu: "33",
    puducherry: "34",
    pondicherry: "34",
    andamanandnicobarislands: "35",
    andamanandnicobar: "35",
    telangana: "36",
    andhrapradesh: "37",
    ladakh: "38",
}

const stateKey = (name: string) =>
    name
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z]/g, "")

/** The GST state code for a state name, or null for a name outside the list. */
export const gstStateCode = (stateName: string | null | undefined) =>
    stateName ? (GST_STATE_CODES[stateKey(stateName)] ?? null) : null

/**
 * The account forms' GSTIN check (Indian addresses only): the GSTIN format, and its first two digits
 * against the selected state's GST code. City is not part of a GSTIN, so only the state is checked.
 * Null when it passes, or when the GSTIN is empty (required-ness is each form's own rule).
 */
export const gstinError = (gstin: string | null | undefined, stateName?: string | null, isIndia = true) => {
    const value = (gstin ?? "").trim().toUpperCase()
    if (!value || !isIndia) return null
    if (!GSTIN_PATTERN.test(value)) return "Enter a valid GSTIN, e.g. 06ABCDE1234F1Z5."
    const expected = gstStateCode(stateName)
    if (expected && value.slice(0, 2) !== expected)
        return `GSTIN should start with ${expected} for ${stateName}; it starts with ${value.slice(0, 2)}.`
    return null
}

/** A Zoho contact id: 10–20 digits (PRD 06 §3). */
export const ZOHO_ID_PATTERN = /^\d{10,20}$/

/** The account form's check on a client's Zoho customer ID; the duplicate check is the server's. */
export const zohoIdError = (id: string) => {
    if (!id) return "Select a Zoho match or enter the ID."
    if (!ZOHO_ID_PATTERN.test(id)) return "Zoho ID should be 10–20 digits."
    return null
}

// Some accounts carry "client" / "vendor" in lower case.
export const isVendorAccount = (accountType: string | null | undefined) => accountType?.toLowerCase() === "vendor"

export const isClientAccount = (accountType: string | null | undefined) => accountType?.toLowerCase() === "client"

/**
 * The account form's identifier field: a vendor keeps its Ref ID (sent as `ref_id`, BUGS-615); every
 * other type gives its Zoho customer ID as `zoho_contact_id` (PRD 06 FE handover).
 */
export const accountIdentifierField = (accountType: string | null | undefined) =>
    isVendorAccount(accountType)
        ? { name: "ref_id" as const, label: "Ref ID", placeholder: "Enter Ref ID" }
        : { name: "zoho_contact_id" as const, label: "Zoho Customer ID", placeholder: "Enter Zoho customer ID" }

/** The Zoho ID column's header: both kinds on the All tab, one on the Client or Vendor tab. */
export const zohoIdHeader = (selectedTab: number) =>
    selectedTab === 1 ? "Zoho Customer ID" : selectedTab === 2 ? "Zoho Vendor ID" : "Zoho Customer / Vendor ID"

export type TZohoMapping = "mapped" | "not_mapped"

export const ZOHO_MAPPING_OPTIONS: { id: TZohoMapping; title: string }[] = [
    { id: "mapped", title: "Mapped" },
    { id: "not_mapped", title: "Not mapped" },
]

/** Doc Hub's document types (PRD 06 §5.3): the `file_type` key the v3 document APIs take, and its label. */
export const ACCOUNT_DOC_TYPES: { id: string; title: string }[] = [
    { id: "accounts_gst_certificate", title: "GST certificate" },
    { id: "accounts_pan_card", title: "PAN card" },
    { id: "accounts_cancelled_cheque", title: "Cancelled cheque" },
    { id: "accounts_agreement", title: "Agreement" },
    { id: "msme_certificate", title: "MSME certificate" },
    { id: "accounts_purchase_order", title: "Purchase order" },
    { id: "accounts_kyc", title: "KYC" },
    { id: "accounts_other", title: "Other" },
]

/** A document type's label; the raw key for one outside the eight. */
export const accountDocTypeLabel = (key: string | null | undefined) =>
    ACCOUNT_DOC_TYPES.find(type => type.id === key)?.title ?? key ?? "-"
